import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'
import { deleteKeyResult, updateKeyResult, updateObjective } from '@/features/planning/api'
import type { ApiError } from '@/lib/api/client'
import type { KeyResult, Objective } from '@/types/planning'

/**
 * Objectives and their key results.
 *
 * A key result the platform can measure shows its live value and says where it
 * came from; one entered by hand shows when it was last touched, so a stale
 * number reads as stale rather than as current.
 */
export function ObjectiveList({ objectives, planId }: { objectives: Objective[]; planId: string }) {
  const { t } = useTranslation()

  if (objectives.length === 0) {
    return (
      <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
        {t('planning.noObjectives')}
      </p>
    )
  }

  return (
    <ol className="flex flex-col gap-3">
      {objectives.map((objective) => (
        <li key={objective.id} className="border-line bg-surface rounded-md border p-4">
          <ObjectiveTitle planId={planId} objective={objective} />
          {objective.owner === null ? null : (
            <p className="text-ink-500 mt-0.5 text-xs">{objective.owner}</p>
          )}
          {objective.narrative === null ? null : (
            <p className="text-ink-700 mt-2 text-sm">{objective.narrative}</p>
          )}

          {objective.keyResults.length === 0 ? null : (
            <ul className="border-line mt-3 flex flex-col gap-3 border-t pt-3">
              {objective.keyResults.map((keyResult) => (
                <li key={keyResult.id}>
                  <KeyResultRow planId={planId} objectiveId={objective.id} keyResult={keyResult} />
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ol>
  )
}

/** An objective's title, correctable in place. */
function ObjectiveTitle({ planId, objective }: { planId: string; objective: Objective }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(objective.title)

  const save = useMutation<void, ApiError, void>({
    mutationFn: () => updateObjective(planId, objective.id, { title: title.trim() }),
    onSuccess: () => {
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: ['plan', planId] })
    },
  })

  if (editing) {
    return (
      <form
        className="flex items-center gap-1.5"
        onSubmit={(event) => {
          event.preventDefault()
          if (title.trim() !== '') save.mutate()
        }}
      >
        <input
          value={title}
          autoFocus
          onChange={(event) => setTitle(event.target.value)}
          className="border-line bg-surface min-w-0 flex-1 rounded border px-2 py-1 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]"
        />
        <button type="submit" disabled={save.isPending} className="bg-brand-600 rounded px-2 py-1 text-[11px] text-white disabled:opacity-40">
          {t('planning.saveEdit')}
        </button>
        <button type="button" onClick={() => { setTitle(objective.title); setEditing(false) }} className="text-ink-500 px-1.5 py-1 text-[11px]">
          {t('planning.cancelEdit')}
        </button>
      </form>
    )
  }

  return (
    <div className="group flex items-center gap-2">
      <h3 className="text-ink-900 min-w-0 flex-1 font-medium">{objective.title}</h3>
      <button
        type="button"
        onClick={() => setEditing(true)}
        title={t('planning.renameObjective')}
        aria-label={t('planning.renameObjective')}
        className="text-ink-500 hover:text-ink-900 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
      >
        <Icon name="pencil" className="size-3" />
      </button>
    </div>
  )
}

function KeyResultRow({
  planId,
  objectiveId,
  keyResult,
}: {
  planId: string
  objectiveId: string
  keyResult: KeyResult
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({
    title: keyResult.title,
    target: String(keyResult.target),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['plan', planId] })

  const save = useMutation<void, ApiError, void>({
    mutationFn: () =>
      updateKeyResult(planId, objectiveId, keyResult.id, {
        title: draft.title.trim(),
        target: Number(draft.target),
      }),
    onSuccess: () => {
      setEditing(false)
      void refresh()
    },
  })

  const remove = useMutation<void, ApiError, void>({
    mutationFn: () => deleteKeyResult(planId, objectiveId, keyResult.id),
    onSuccess: () => void refresh(),
  })

  const { t } = useTranslation()

  const current = keyResult.current ?? 0
  const baseline = keyResult.baseline ?? 0
  const span = keyResult.target - baseline
  const progress = span === 0 ? 100 : ((current - baseline) / span) * 100
  const clamped = Math.max(0, Math.min(100, progress))

  return (
    <div>
      {editing ? (
        <form
          className="flex flex-wrap items-center gap-1.5"
          onSubmit={(event) => {
            event.preventDefault()
            if (draft.title.trim() !== '') save.mutate()
          }}
        >
          <input
            value={draft.title}
            autoFocus
            onChange={(event) => setDraft({ ...draft, title: event.target.value })}
            className="border-line bg-surface min-w-40 flex-1 rounded border px-2 py-1 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]"
          />
          <input
            value={draft.target}
            inputMode="decimal"
            aria-label={t('planning.target')}
            onChange={(event) => setDraft({ ...draft, target: event.target.value })}
            className="border-line bg-surface w-20 rounded border px-2 py-1 font-mono text-[13px] tabular-nums outline-none focus:border-[color:var(--color-brand-300)]"
          />
          <button type="submit" disabled={save.isPending} className="bg-brand-600 rounded px-2 py-1 text-[11px] text-white disabled:opacity-40">
            {t('planning.saveEdit')}
          </button>
          <button type="button" onClick={() => setEditing(false)} className="text-ink-500 px-1.5 py-1 text-[11px]">
            {t('planning.cancelEdit')}
          </button>
        </form>
      ) : (
        <div className="group flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-ink-900 text-sm">{keyResult.title}</span>
          <span className="text-ink-700 ms-auto font-mono text-sm tabular-nums">
            {formatNumber(keyResult.current)} / {formatNumber(keyResult.target)}
            {keyResult.unit === null ? '' : ` ${keyResult.unit}`}
          </span>
          <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button type="button" onClick={() => setEditing(true)} title={t('planning.renameKeyResult')} aria-label={t('planning.renameKeyResult')} className="text-ink-500 hover:text-ink-900 rounded p-1">
              <Icon name="pencil" className="size-3" />
            </button>
            <button type="button" onClick={() => remove.mutate()} title={t('planning.removeKeyResult')} aria-label={t('planning.removeKeyResult')} className="text-ink-500 hover:text-[color:var(--color-state-blocked)] rounded p-1">
              <Icon name="trash" className="size-3" />
            </button>
          </span>
        </div>
      )}

      <div className="bg-surface-2 mt-1.5 h-1.5 overflow-hidden rounded-full">
        <span
          className="bg-brand-600 block h-full rounded-full"
          style={{ width: `${clamped}%` }}
        />
      </div>

      <p className="text-ink-500 mt-1 text-xs">
        {keyResult.isComputed ? (
          <>
            <span className="text-brand-600">{t('planning.computed')}</span> — {keyResult.sourceLabel}
          </>
        ) : keyResult.measuredAt === null ? (
          t('planning.manual')
        ) : (
          t('planning.measuredAt', { date: keyResult.measuredAt.slice(0, 10) })
        )}
      </p>
    </div>
  )
}

function formatNumber(value: number | null): string {
  if (value === null) return '—'

  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}
